import React, { useEffect, useRef, useState } from 'react';
import { Typography, Paper, Button, TextField, FormControlLabel, Checkbox, Box, Divider } from '@mui/material';
import { Window as WindowIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, AuthStatus } from '../../context/auth-provider';
import ChangePassword from '../../components/change-pw';
import Mfa from '../../components/mfa';
import MfaEntry from '../../components/mfa-entry';
import LoadingSpinner from '../../components/loading-spinner';
import { MfaMode } from '../../data/enums';
import hcrcLogo from '../../assets/images/hce_logo_1.png';
import Copyright from '../../components/copyright';

export default function SignInView() {
  const { user, login, loginWithSSO, qrCode, validateMFA, confirmMFA, completeNewPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState<string>('');
  const [authChallenge, setAuthChallenge] = useState<AuthStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  // loadingPhase values:
  // 'MFA_VERIFY'   -> during initial MFA setup QR code verification
  // 'MFA_CONFIRM'  -> during TOTP submission for existing MFA (MFA_REQUIRED)
  // 'POST_AUTH'    -> after successful code while waiting for session/user context hydration
  const [loadingPhase, setLoadingPhase] = useState<'MFA_VERIFY' | 'MFA_CONFIRM' | 'POST_AUTH' | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) {
      navigate('/ama', { replace: true });
    }
  }, [user, navigate]);

  // Show error from Cognito OAuth callback (e.g. SAML attribute mismatch)
  useEffect(() => {
    const state = location.state as { oauthError?: string } | null;
    if (state?.oauthError) {
      setError(state.oauthError);
      // Clear the state so error doesn't persist on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  // Allow page to scroll on the login page — the global CSS sets overflow-y: hidden
  // on html/body/#root for the dashboard, but the login page needs to scroll when
  // the viewport is short (e.g. devtools docked at bottom).
  useEffect(() => {
    const els = [document.documentElement, document.body];
    const prev = els.map(el => el.style.overflowY);
    els.forEach(el => { el.style.overflowY = 'auto'; });
    return () => {
      els.forEach((el, i) => { el.style.overflowY = prev[i]; });
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const data = new FormData(event.currentTarget);
    const email = data.get('email') as string;
    const password = data.get('password') as string;

    if (email && password) {
      setUsername(email); // Store username for subsequent challenges
      const status = await login(email, password);
      setAuthChallenge(status);

      if (status === 'FAILURE') {
        setError('Login failed. Please check your email and password.');
      }
      
      if (passwordRef.current) {
        passwordRef.current.value = '';
      }
    }
  };

  const handleMfaSetup = async (code: string) => {
    setError(null);
    setLoadingPhase('MFA_VERIFY');
    const success = await validateMFA(code);
    if (!success) {
      setLoadingPhase(null);
      setError('Invalid verification code. Please try again.');
      return;
    }
    // Don't set SUCCESS prematurely; wait for user context to populate.
    setLoadingPhase('POST_AUTH');
  };

  const handleMfaConfirm = async (code: string) => {
    setError(null);
    setLoadingPhase('MFA_CONFIRM');
    const success = await confirmMFA(code);
    if (!success) {
      setLoadingPhase(null);
      setError('Your authentication information is incorrect. Please try again.');
      return;
    }
    setLoadingPhase('POST_AUTH');
  };

  const handleNewPasswordSubmit = async (payload: IChangePasswordPayload) => {
    setError(null);
    const status = await completeNewPassword(payload.newPassword);
    setAuthChallenge(status);
    if (status === 'FAILURE') {
      setError('Failed to update password. Please try again.');
    }
    // If status is MFA_SETUP or MFA_REQUIRED, component re-renders appropriate view
  };

  return (
    <Box
      component="main"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%',
        px: { xs: 2, sm: 3 },
        py: 4,
        boxSizing: 'border-box',
        // Override the global overflow-y: hidden set on html/body/#root
        // so the login page can scroll when the viewport is short (e.g. devtools open)
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: { xs: 3, sm: 4 },   // tighter padding on mobile
          width: '100%',
          maxWidth: '400px',
        }}
      >
        <Box
          component="img"
          src={hcrcLogo}
          alt="Human Capital Education"
          sx={{ width: '100%', maxWidth: '300px', height: 'auto', mb: 4 }}
        />

        {/* Error Display */}
        {error && <Typography sx={{ color: 'error.main', mb: 2, textAlign: 'center' }}>{error}</Typography>}

        {/* Password Change View */}
        {authChallenge === 'NEW_PASSWORD_REQUIRED' && (
          <ChangePassword username={username} onSubmit={handleNewPasswordSubmit} />
        )}

        {/* MFA Setup View */}
        {authChallenge === 'MFA_SETUP' && qrCode && !loadingPhase && (
          <Mfa mode={MfaMode.ScanQrCode} qrCodeValue={qrCode} onCodeEntered={handleMfaSetup} />
        )}
        {authChallenge === 'MFA_SETUP' && loadingPhase && (
          <LoadingSpinner
            message={
              loadingPhase === 'MFA_VERIFY'
                ? 'Verifying code…'
                : 'Finalizing session…'
            }
          />
        )}

        {/* MFA Entry View (existing MFA) */}
        {authChallenge === 'MFA_REQUIRED' && (
          <>
            <MfaEntry onCodeEntered={handleMfaConfirm} loading={!!loadingPhase} />
            {loadingPhase && (
              <LoadingSpinner
                message={
                  loadingPhase === 'MFA_CONFIRM'
                    ? 'Confirming code…'
                    : 'Finalizing session…'
                }
              />
            )}
          </>
        )}

        {/* Default Login View */}
        {(!authChallenge || authChallenge === 'FAILURE') && !loadingPhase && (
          <Box sx={{ width: '100%' }}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 1, width: '100%' }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                inputRef={passwordRef}
              />
              <FormControlLabel
                control={<Checkbox value="remember" color="primary" />}
                label="Remember me"
                sx={{ alignSelf: 'flex-start' }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2, mb: 2 }}
              >
                Sign In
              </Button>
            </Box>

            <Divider sx={{ width: '100%', my: 1 }}>or</Divider>

            <Button
              fullWidth
              variant="outlined"
              onClick={loginWithSSO}
              startIcon={<WindowIcon />}
              sx={{ mt: 1 }}
            >
              Sign in with Microsoft
            </Button>
          </Box>
        )}
        {loadingPhase && (!user && (authChallenge === null || authChallenge === 'FAILURE')) && (
          <LoadingSpinner message="Loading…" />
        )}
      </Paper>

      <Copyright />
    </Box>
  );
}